import { i18nNsFactory } from '@atlas-frontend/i18n';
import React, { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  filterOption,
  FormDateRangePicker,
  FormField,
  FormSelect,
} from '../../common/form';
import translations from '../translations/AlternativeAlgorithmExceptionForm.translations.json';
import { DynamicFormRow } from './DynamicFormRow';
import { AlgorithmSetOption } from './types';
import { createDefaultException } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternative-algorithm-exception-form',
);
addTranslations(translations);

export interface AlternativeAlgorithmExceptionFormProps {
  count: number;
  index: number;
  options: AlgorithmSetOption[];
  onViewAlternative?: (index: number) => void;
}

export function AlternativeAlgorithmExceptionFields({
  count,
  index,
  options,
  onViewAlternative,
}: AlternativeAlgorithmExceptionFormProps) {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const selected = useWatch({
    name: `exceptions.${index}.alternativeId`,
  });

  const onAddAlternative = useCallback(() => {
    const exceptions = [...getValues('exceptions')];
    setValue('exceptions', exceptions.concat(createDefaultException()), {
      shouldDirty: true,
    });
  }, [getValues, setValue]);
  const onRemoveAlternative = useCallback(() => {
    let exceptions = [...getValues('exceptions')];
    if (count < 2) {
      exceptions = [createDefaultException()];
      // Reset period because it is not replaced if only period object is replaced
      setValue('exceptions.0.validPeriod', [null, null], { shouldDirty: true });
    } else {
      exceptions.splice(index, 1);
    }
    setValue('exceptions', exceptions, { shouldDirty: true });
  }, [count, getValues, index, setValue]);

  return (
    <DynamicFormRow
      count={count}
      index={index}
      onAddAlternative={onAddAlternative}
      onRemoveAlternative={onRemoveAlternative}
      onViewAlternative={selected ? onViewAlternative : undefined}
    >
      <FormField
        hiddenLabel={index > 0}
        label={t('exception.validPeriod.label')}
        name={`exceptions.${index}.validPeriod`}
        required={index === 0}
      >
        <FormDateRangePicker name={`exceptions.${index}.validPeriod`} />
      </FormField>
      <FormField
        hiddenLabel={index > 0}
        label={t('exception.alternativeId.label')}
        name={`exceptions.${index}.alternativeId`}
        required
      >
        <SelectWrapper>
          <FormSelect
            name={`exceptions.${index}.alternativeId`}
            placeholder={t('exception.alternativeId.placeholder')}
            options={[...options.values()]}
            filterOption={filterOption}
            showSearch
          />
        </SelectWrapper>
      </FormField>
    </DynamicFormRow>
  );
}

const SelectWrapper = styled.div`
  min-width: 200px;
`;
